/**
 * RefFieldWidget — Autocomplete for ref/enum_ref fields, options via /lookup.
 * Uses the target object's presentation_field or presentation_template from Designer.
 * Shared by RuntimeFormRenderer (record forms) and ScanImportDialog (intake review grid).
 *
 * Optional onOpenRef callback allows opening the referenced record (e.g., navigate to detail form).
 */
import React, { useEffect, useRef, useState } from 'react'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import apiClient from '@/utils/api'
import type { AttributeDef } from '../designer/services/configObjectApi'
import { resolveLabel } from './resolveLabel'
import { createTtlCache } from './ttlCache'
import { useObjectLabel } from './useObjectLabel'

// Lazy: a static import pulls RuntimeListView → react-router-dom, and that
// tarball has no package.json main. Existing widget tests never open the
// dialog and must keep loading without a virtual router mock.
const RefChoiceDialog = React.lazy(() =>
  import('./RefChoiceDialog').then((m) => ({ default: m.RefChoiceDialog })),
)

export interface RefLookupOption {
  id: string
  display: string
  synonyms?: Record<string, string>
}

/**
 * Three fields pointing at the same catalog used to fetch its lookup three
 * times on every form mount. Cached per (connection, target, locale) with
 * in-flight de-duplication — same 30s staleness window as the designer tree.
 *
 * The fetch stays on mount rather than deferring to dropdown-open: the label
 * of the CURRENTLY SELECTED value is resolved out of `options`, so a field
 * whose dropdown is never opened would otherwise render blank.
 */
const LOOKUP_TTL_MS = 30_000
/** Keystroke → refetch delay. Matches the list search box. */
const SEARCH_DEBOUNCE_MS = 250
const lookupCache = createTtlCache<RefLookupOption[]>(LOOKUP_TTL_MS)

export function invalidateRefLookupCache(key?: string): void {
  lookupCache.invalidate(key)
}

export function resolveRefLookupTarget(
  attr: Pick<AttributeDef, 'props'>,
  selfObjectName?: string,
): string {
  const raw = String(attr.props?.target || attr.props?.referenceTarget || '').trim()
  if (!raw) return ''
  if (raw.toLowerCase() === 'self') return (selfObjectName || '').trim()
  return raw
}

export function RefFieldWidget({
  attr,
  value,
  onChange,
  disabled,
  connectionId,
  isRequired,
  onOpenRef,
  selfObjectName,
  compact = false,
}: {
  attr: AttributeDef
  value: any
  onChange: (v: any) => void
  disabled: boolean
  connectionId: string
  isRequired: boolean
  onOpenRef?: (target: string, id: string) => void
  /** Owner object name — resolves hierarchical parent_id props.target === 'self'. */
  selfObjectName?: string
  /** Tabular cell mode — standard (borderless) field + quieter adornments. */
  compact?: boolean
}) {
  const { locale, defaultCode } = useAppLocale()
  const [options, setOptions] = useState<RefLookupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  /** Display of the stored value when it falls outside the current result set. */
  const [resolvedValue, setResolvedValue] = useState<RefLookupOption | null>(null)
  /** Locale the current `resolvedValue` was fetched for — skip-guard must not
   *  survive a language switch (display text is locale-resolved server-side). */
  const resolvedLocaleRef = useRef<string | null>(null)
  const [optionsReady, setOptionsReady] = useState(false)
  const [choiceOpen, setChoiceOpen] = useState(false)
  const target = resolveRefLookupTarget(attr, selfObjectName)
  const attrType = String(attr.attr_type ?? '').toLowerCase()
  const isEnumTarget = attrType === 'enum_ref' || attrType.includes('enum')
  // Placeholder must read as a human name in the active language, not the raw
  // configuration name of the target object.
  const targetLabel = useObjectLabel(connectionId, target, locale ?? '', defaultCode)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
  }, [])

  useEffect(() => {
    if (!target || !connectionId) return
    let cancelled = false
    setLoading(true)
    setOptionsReady(false)
    const q = debouncedSearch.trim()
    lookupCache
      .get(`${connectionId}|${target}|${locale ?? ''}|${q}`, () =>
        apiClient
          .get(
            `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(target)}/lookup` +
              `?locale=${encodeURIComponent(locale ?? '')}&search=${encodeURIComponent(q)}`,
          )
          .then((res: any) => {
            const rows = res?.data?.data || []
            return Array.isArray(rows) ? (rows as RefLookupOption[]) : []
          }),
      )
      .then((rows) => {
        if (cancelled) return
        setOptions(rows)
        setOptionsReady(true)
      })
      .catch(() => {
        if (cancelled) return
        setOptions([])
        setOptionsReady(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [target, connectionId, locale, debouncedSearch])

  // The stored value may sit outside the browse window (a 1001-row chart of
  // accounts returns 200) or outside the active search result. Resolve it by
  // id so the field never renders blank for a value it actually holds.
  useEffect(() => {
    if (!target || !connectionId || !value) {
      setResolvedValue(null)
      resolvedLocaleRef.current = null
      return
    }
    if (!optionsReady) return
    if (options.some((o) => o.id === value)) { setResolvedValue(null); return }
    if (resolvedValue?.id === value && resolvedLocaleRef.current === (locale ?? '')) return
    let cancelled = false
    apiClient
      .get(
        `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(target)}/lookup` +
          `?locale=${encodeURIComponent(locale ?? '')}&ids=${encodeURIComponent(String(value))}`,
      )
      .then((res: any) => {
        if (cancelled) return
        const rows = res?.data?.data || []
        resolvedLocaleRef.current = locale ?? ''
        setResolvedValue(Array.isArray(rows) && rows[0] ? (rows[0] as RefLookupOption) : null)
      })
      .catch(() => { if (!cancelled) setResolvedValue(null) })
    return () => { cancelled = true }
    // `resolvedValue` is read as a guard only — including it would re-run on every set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, connectionId, locale, value, options, optionsReady])

  const selected =
    options.find((o) => o.id === value) ||
    (resolvedValue && resolvedValue.id === value ? resolvedValue : null)
  const displayOptions =
    selected && !options.some((o) => o.id === selected.id)
      ? [selected, ...options]
      : options
  const selectedLabel = selected
    ? resolveLabel({ synonyms: selected.synonyms, title: selected.display, name: selected.id }, locale, defaultCode)
    : ''
  useEffect(() => {
    setInputValue(selectedLabel)
  }, [selectedLabel])

  return (
    <>
    <Autocomplete
      size="small"
      options={displayOptions}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_e, v, reason) => {
        if (reason === 'reset') return
        setInputValue(v)
        if (reason === 'clear') {
          if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
          setDebouncedSearch('')
          return
        }
        if (reason !== 'input') return
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => setDebouncedSearch(v), SEARCH_DEBOUNCE_MS)
      }}
      filterOptions={(opts) => opts}
      onChange={(_e, opt) => onChange(opt?.id ?? null)}
      getOptionLabel={(o) => resolveLabel({ synonyms: o.synonyms, title: o.display, name: o.id }, locale, defaultCode)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      disabled={disabled}
      fullWidth
      slotProps={
        compact
          ? {
              popupIndicator: { className: 'tabular-cell-adornment' },
              clearIndicator: { className: 'tabular-cell-adornment' },
            }
          : undefined
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {resolveLabel({ synonyms: option.synonyms, title: option.display, name: option.id }, locale, defaultCode)}
        </li>
      )}
      renderInput={(params) => {
        const openRefButton =
          value && onOpenRef && target && !isEnumTarget ? (
          <IconButton
            size="small"
            className={compact ? 'tabular-cell-adornment' : undefined}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onOpenRef(target, value)
            }}
            aria-label="Open referenced record"
            sx={{ color: 'text.secondary' }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        ) : null

        // Enum targets have a handful of non-hierarchical values — the
        // dropdown is already the right tool, a choice form would be noise.
        const choiceButton =
          target && !isEnumTarget && !disabled ? (
            <IconButton
              size="small"
              className={compact ? 'tabular-cell-adornment' : undefined}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setChoiceOpen(true)
              }}
              aria-label="Open choice form"
              title="Open choice form (F4)"
              sx={{ color: 'text.secondary' }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          ) : null

        return (
          <TextField
            {...params}
            required={isRequired}
            variant={compact ? 'standard' : 'outlined'}
            placeholder={target ? `Select ${targetLabel}...` : ''}
            onKeyDown={(e) => {
              if (e.key === 'F4' && target && !isEnumTarget && !disabled) {
                e.preventDefault()
                setChoiceOpen(true)
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {choiceButton}
                  {openRefButton}
                  {params.InputProps?.endAdornment}
                </>
              ),
            }}
          />
        )
      }}
    />
      {choiceOpen && target ? (
        <React.Suspense fallback={null}>
          <RefChoiceDialog
            open={choiceOpen}
            target={target}
            connectionId={connectionId}
            onPick={(id) => onChange(id)}
            onClose={() => setChoiceOpen(false)}
          />
        </React.Suspense>
      ) : null}
    </>
  )
}
